import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_BASE_URL } from "../config/constants";

const DEFAULT_REPORT = (date) => ({
  date,
  restaurant: "",
  total_orders: 0,
  total_quantity: 0,
  total_amount: 0,
  total_credit_amount: 0,
  modes: [],
});

const PaymentMethodReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(DEFAULT_REPORT(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/report-v2/payment-methods`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, date }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load report");
      setReport({ ...DEFAULT_REPORT(date), ...json, modes: Array.isArray(json?.modes) ? json.modes : [] });
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setReport(DEFAULT_REPORT(date));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const normalizedModes = useMemo(() => {
    const base = {
      cash: { label: "Cash" },
      card: { label: "Card" },
      upi: { label: "UPI" },
      credit: { label: "Credit" },
    };
    const incoming = report?.modes || [];
    const map = {};
    incoming.forEach((m) => {
      const key = String(m.mode || "").toLowerCase();
      map[key] = {
        key,
        label: m.mode || base[key]?.label || "Other",
        total_orders: Number(m.total_orders || 0),
        total_quantity: Number(m.total_quantity || 0),
        total_amount: Number(m.total_amount || 0),
        share: Number(m.share || 0),
      };
    });
    return Object.keys(base).map((key) => ({
      key,
      label: base[key].label,
      total_orders: map[key]?.total_orders || 0,
      total_quantity: map[key]?.total_quantity || 0,
      total_amount: map[key]?.total_amount || 0,
      share: map[key]?.share || 0,
    }));
  }, [report]);

  const exportCsv = () => {
    const rows = normalizedModes.map((m, idx) => [
      idx + 1,
      m.label,
      m.total_orders,
      m.total_quantity,
      m.total_amount,
      `${m.share}%`,
    ]);
    downloadCSV(
      `payment_methods_${date}.csv`,
      ["#", "Payment Mode", "Orders", "Qty", "Amount", "Share"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = normalizedModes.map((m, idx) => [
      idx + 1,
      m.label,
      m.total_orders,
      m.total_quantity,
      m.total_amount,
      `${m.share}%`,
    ]);
    downloadExcel(
      `payment_methods_${date}.xlsx`,
      ["#", "Payment Mode", "Orders", "Qty", "Amount", "Share"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `payment_methods_${date}.pdf`);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Payment Method Report"
        subtitle="Sales breakdown by cash, card, UPI, and credit."
        stats={[
          { label: "Total Orders", value: report.total_orders || 0 },
          { label: "Total Qty", value: report.total_quantity || 0 },
          { label: "Total Sales", value: fmtINR(report.total_amount) },
          { label: "Total Credit", value: fmtINR(report.total_credit_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker date={date} onDateChange={(d) => setDate(d)} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : normalizedModes.length === 0 ? (
          <Center>No data for this date.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Payment Mode</th>
                  <th>Orders</th>
                  <th>Qty</th>
                  <th>Amount</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {normalizedModes.map((m, idx) => (
                  <tr key={m.key}>
                    <td>{idx + 1}</td>
                    <td><strong>{m.label}</strong></td>
                    <td>{m.total_orders}</td>
                    <td>{m.total_quantity}</td>
                    <td>{fmtINR(m.total_amount)}</td>
                    <td>{m.share}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(reportRef.current, `payment_methods_${date}`)}>
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default PaymentMethodReportPage;

const today = () => toISO(new Date());
const toISO = (d) => toLocalYmd(d);

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #075f9e, #0786c5);
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;
